import React, { useState, useEffect, useCallback, useRef } from 'react';
import LifeAssistantService from './services/LifeAssistantService';
import './App.css';
import './WorkoutTab.css';

const NEWS_SWIPE_THRESHOLD = 90;

const fmtNum = (v, casas = 0) =>
  (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });

// "5h", "3min", "2d" — tempo relativo desde a publicação.
const fmtRelativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMin = Math.max(0, Math.floor((Date.now() - then) / 60000));
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin}min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD}d`;
};

// Card de notícia estilo feed aberto (ícone/fonte + manchete + imagem,
// tempo relativo embaixo) — clique abre a matéria direto (nova aba),
// arrastar descarta. Sem expansão inline: era isso que fazia a tela
// "tremer" (mudança de altura do card empurrando o resto da lista).
function NewsArticleCard({ article, dismissed, onDismiss }) {
  const [dragX, setDragX] = useState(0);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const movedRef = useRef(false);

  const handlePointerDown = (e) => {
    if (dismissed) return;
    draggingRef.current = true;
    movedRef.current = false;
    startXRef.current = e.clientX;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current) return;
    const delta = e.clientX - startXRef.current;
    if (Math.abs(delta) > 6) movedRef.current = true;
    setDragX(delta);
  };

  const endDrag = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (Math.abs(dragX) > NEWS_SWIPE_THRESHOLD) onDismiss();
    setDragX(0);
  };

  const handleClick = (e) => {
    if (movedRef.current) {
      e.preventDefault();
      movedRef.current = false;
      return;
    }
    if (article.url) window.open(article.url, '_blank', 'noopener,noreferrer');
  };

  const dragStyle = draggingRef.current && dragX !== 0
    ? { transform: `translateX(${dragX}px)`, transition: 'none' }
    : undefined;

  return (
    <div className={`news-item-wrap ${dismissed ? 'news-item-dismissed' : ''}`}>
      <div className="news-swipe-hint" aria-hidden="true">🗑 Remover</div>
      <div
        className="news-item"
        style={dragStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <button className="news-item-head" onClick={handleClick}>
          <span className="news-item-main">
            {article.source && (
              <span className="news-item-source">{article.emoji || '📰'} {article.source}</span>
            )}
            <span className="news-title-v2">{article.title}</span>
            {article.published_at && (
              <span className="news-time">{fmtRelativeTime(article.published_at)}</span>
            )}
          </span>
          {article.image && (
            <img className="news-thumb-v2" src={article.image} alt="" loading="lazy" />
          )}
        </button>
      </div>
    </div>
  );
}

function NewsTab({ svc }) {
  const [finance, setFinance] = useState(null);
  const [marketOpen, setMarketOpen] = useState(false);
  const [news, setNews] = useState(null);
  const [newsBusy, setNewsBusy] = useState(false);
  const [newsDismissed, setNewsDismissed] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [financeData, newsData] = await Promise.all([
        LifeAssistantService.getFinance(),
        LifeAssistantService.getNews(),
      ]);
      setFinance(financeData);
      setNews(newsData);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [svc]);

  useEffect(() => { load(); }, [load]);

  const refreshNews = async () => {
    setNewsBusy(true);
    try {
      const d = await LifeAssistantService.getNews(true);
      setNews(d);
      setNewsDismissed({});
    } catch (e) { /* silêncio */ } finally { setNewsBusy(false); }
  };

  const dismissNews = (chave) => {
    setNewsDismissed((p) => ({ ...p, [chave]: true }));
  };

  if (loading && !finance && !news) {
    return <div className="wk-view"><p className="wk-msg">Carregando…</p></div>;
  }

  return (
    <div className="wk-view">
      {error && <p className="wk-msg wk-err">{error}</p>}

      {/* Mercado — mesma faixa rolando, clique abre a lista parada. */}
      {finance?.indicators?.length > 0 && (
        <section
          className="card market-card"
          onClick={() => setMarketOpen((v) => !v)}
          role="button"
          tabIndex={0}
        >
          <h3>💹 Mercado</h3>
          <div className="mk-ticker-wrap">
            <div className="mk-ticker-track">
              {[...finance.indicators, ...finance.indicators].map((ind, idx) => (
                <div className="mk-ticker-item" key={`${ind.key}-${idx}`}>
                  <span className="mk-ticker-label">{ind.emoji} {ind.label}</span>
                  <span className="mk-ticker-value">{ind.value}</span>
                  {ind.change != null && (
                    <span className={`mk-ticker-change ${ind.change > 0 ? 'up' : ind.change < 0 ? 'down' : ''}`}>
                      {ind.change > 0 ? '▲' : ind.change < 0 ? '▼' : ''}{fmtNum(Math.abs(ind.change), 2)}%
                    </span>
                  )}
                  {ind.extra_value && (
                    <span className="mk-ticker-extra">({ind.extra_label}: {ind.extra_value})</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {marketOpen && (
            <div className="mk-list" onClick={(e) => e.stopPropagation()}>
              {finance.indicators.map((ind) => (
                <div className="mk-list-row" key={ind.key}>
                  <span className="mk-list-label">{ind.emoji} {ind.label}</span>
                  <span className="mk-list-right">
                    <span className="mk-list-value">{ind.value}</span>
                    {ind.change != null && (
                      <span className={`mk-ticker-change ${ind.change > 0 ? 'up' : ind.change < 0 ? 'down' : ''}`}>
                        {ind.change > 0 ? '▲' : ind.change < 0 ? '▼' : ''}{fmtNum(Math.abs(ind.change), 2)}%
                      </span>
                    )}
                    {ind.extra_value && (
                      <span className="mk-ticker-extra">({ind.extra_label}: {ind.extra_value})</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Notícias — lista aberta por categoria, sem recolher (pedido do usuário). */}
      {news && news.groups?.length > 0 && (
        <section className="card news-card">
          <div className="analise-head">
            <h3>📰 Notícias</h3>
            <button className="analise-refresh" onClick={refreshNews} disabled={newsBusy}>
              {newsBusy ? '…' : 'Atualizar'}
            </button>
          </div>
          <div className="news-groups">
            {news.groups.map((group) => (
              <div className="news-group" key={group.topic}>
                <div className="news-category-label">{group.emoji} {group.topic}</div>
                {group.articles.map((article, idx) => {
                  const chave = `${group.topic}-${idx}`;
                  const dismissKey = article.url || chave;
                  return (
                    <NewsArticleCard
                      key={chave}
                      article={article}
                      dismissed={!!newsDismissed[dismissKey]}
                      onDismiss={() => dismissNews(dismissKey)}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="wk-spacer" />
    </div>
  );
}

export default NewsTab;
